// src/components/casino/GameSessionModal.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const GameSessionModal = ({
  isOpen,
  game,
  betAmount,
  setBetAmount,
  usdBalance,
  onStart,
  onCancel,
  loading,
  btcPrice
}) => {
  const navigate = useNavigate();
  const [amountError, setAmountError] = useState('');
  const [btcEquivalent, setBtcEquivalent] = useState(0);

  useEffect(() => {
    if (betAmount > 0 && btcPrice) {
      setBtcEquivalent(betAmount / btcPrice);
    } else {
      setBtcEquivalent(0);
    }
  }, [betAmount, btcPrice]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onCancel();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onCancel]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !game) return null;

  const minBet = game.min_bet || 0.01;
  const maxBet = game.max_bet || 200;
  const hasSufficientBalance = usdBalance >= minBet;

  const handleBetChange = (e) => {
    const value = parseFloat(e.target.value) || 0;
    setBetAmount(value);
    
    if (value < minBet) {
      setAmountError(`Minimum bet is $${minBet}`);
    } else if (value > maxBet) {
      setAmountError(`Maximum bet is $${maxBet}`);
    } else if (value > usdBalance) {
      setAmountError(`Insufficient balance. Available: $${usdBalance.toFixed(2)}`);
    } else {
      setAmountError('');
    }
  };

  const handleQuickBet = (amount) => {
    setBetAmount(amount);
  };

  const handleStart = () => {
    if (amountError) {
      window.alert(amountError);
      return;
    }
    onStart(betAmount);
  };

  const handleDeposit = () => {
    navigate('/deposit');
    onCancel(); // Close the modal when navigating
  };

  const handleGoToWallet = () => {
    navigate('/wallet');
    onCancel(); // Close the modal when navigating
  };

  // Check if the current bet amount is valid (including balance check)
  const isBetInvalid = betAmount > usdBalance || betAmount < minBet || betAmount > maxBet || !betAmount;
  const isPlayDisabled = loading || !!amountError || !hasSufficientBalance || isBetInvalid;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
      <div className="bg-gray-900 rounded-2xl max-w-md w-full p-6 relative border border-gray-800 max-h-[90vh] overflow-y-auto">
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          ✕
        </button>
        
        <div className="text-center mb-6">
          <div className="text-4xl mb-2">🎰</div>
          <h3 className="text-xl font-bold text-white">{game.name}</h3>
          <p className="text-gray-400 text-sm">{game.provider}</p>
        </div>
        
        {/* Clickable Balance Card - Navigates to /wallet */}
        <div 
          onClick={handleGoToWallet}
          className="bg-gray-800 rounded-lg p-4 mb-6 cursor-pointer hover:bg-gray-750 transition-colors group relative"
        >
          <div className="flex justify-between items-center">
            <span className="text-gray-400 group-hover:text-gray-300 transition-colors flex items-center gap-2">
              Available Balance
              <svg className="w-4 h-4 text-gray-500 group-hover:text-gray-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
              </svg>
            </span>
            <span className={`font-bold ${hasSufficientBalance ? 'text-white' : 'text-red-400'} group-hover:scale-105 transition-transform`}>
              ${usdBalance.toFixed(2)} USD
              {!hasSufficientBalance && (
                <span className="text-xs ml-2 text-red-400">⚠️ Low Balance</span>
              )}
            </span>
          </div>
          {btcPrice && (
            <div className="flex justify-between items-center text-sm mt-1">
              <span className="text-gray-500">BTC Price</span>
              <span className="text-gray-300">1 BTC = ${btcPrice.toFixed(2)}</span>
            </div>
          )}
          
          {/* Hover indicator */}
          <div className="absolute inset-0 rounded-lg border-2 border-transparent group-hover:border-purple-500/30 transition-colors pointer-events-none"></div>
          
          {/* Tooltip on hover */}
          <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <div className="bg-gray-700 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
              Click to view wallet
            </div>
          </div>
        </div>
        
        <div className="mb-6">
          <label className="block text-gray-300 text-sm font-medium mb-2">
            Enter Bet Amount (USD)
          </label>
          
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
            <input
              type="number"
              value={betAmount}
              onChange={handleBetChange}
              min={minBet}
              max={Math.min(maxBet, usdBalance)}
              step={0.01}
              disabled={!hasSufficientBalance}
              className={`w-full bg-gray-800 text-white text-lg pl-8 pr-4 py-3 rounded-lg border ${
                !hasSufficientBalance ? 'border-red-500 opacity-50' : 'border-gray-700 focus:border-purple-500'
              } focus:outline-none focus:ring-1 focus:ring-purple-500`}
            />
          </div>
          
          <div className="flex gap-2 mt-3 flex-wrap">
            {[1, 5, 10, 25, 50, 100].filter(amount => amount <= maxBet && amount <= usdBalance).map(amount => (
              <button
                key={amount}
                onClick={() => handleQuickBet(amount)}
                disabled={!hasSufficientBalance}
                className={`bg-gray-800 hover:bg-gray-700 text-gray-300 px-4 py-1 rounded text-sm transition-colors ${
                  !hasSufficientBalance ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                ${amount}
              </button>
            ))}
          </div>
          
          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>Min: ${minBet}</span>
            <span>Max: ${Math.min(maxBet, usdBalance)}</span>
          </div>
          
          {btcEquivalent > 0 && (
            <div className="mt-2 text-sm text-gray-400">
              ≈ {btcEquivalent.toFixed(8)} BTC
            </div>
          )}
          
          {amountError && (
            <div className="mt-2 text-red-400 text-sm animate-pulse">
              ⚠️ {amountError}
            </div>
          )}
          
          {!hasSufficientBalance && (
            <div className="mt-2 text-red-400 text-sm animate-pulse">
              ⚠️ Insufficient balance. Minimum bet is ${minBet}
            </div>
          )}
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 bg-gray-800 hover:bg-gray-700 text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          
          {!hasSufficientBalance ? (
            <button
              onClick={handleDeposit}
              className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white py-3 rounded-lg font-semibold transition-all"
            >
              💰 Add Funds
            </button>
          ) : (
            <button
              onClick={handleStart}
              disabled={isPlayDisabled}
              className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white py-3 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Starting...
                </span>
              ) : (
                '🎮 Play Now'
              )}
            </button>
          )}
        </div>
        
        <p className="text-xs text-gray-500 text-center mt-4">
          🔒 Secure session • Your balance will be locked during gameplay
        </p>
      </div>
    </div>
  );
};

export default GameSessionModal;